import React from 'react'
import { useTranslation } from 'react-i18next'
import { useAppSelect } from 'src/hooks/useApp'
import { IUserLocation } from 'src/services/auth/users'
import { getObjectsById } from 'src/services/storage/project/scheme/objects'

/**
 * Presence carries WHERE a teammate is (a path and an object reference), never what it is called.
 * The names are resolved here, on the viewer's side:
 *  - the page out of the viewer's own routes and translations;
 *  - the object through the storage API, which checks the viewer's permissions. A record the
 *    viewer has no access to stays "a contact", its name is never shown.
 */

const PAGE_KEYS: [RegExp, string][] = [
  [/^\/$/, 'dashboard'],
  [/^\/callHistory/, 'callHistory'],
  [/^\/marketing/, 'marketing'],
  [/^\/email/, 'email'],
  [/^\/forms/, 'forms'],
  [/^\/templates/, 'templates'],
  [/^\/(settings|admin)/, 'settings'],
  [/^\/profile/, 'profile'],
  [/^\/storage/, 'storage'],
]

// one lookup per object per page load, shared by every row that shows it; null = no access / gone
const titleCache = new Map<string, Promise<string | null>>()

const resolveObjectTitle = (scheme: string, uniqId: string) => {
  const key = `${scheme}/${uniqId}`

  if (!titleCache.has(key)) {
    titleCache.set(
      key,
      getObjectsById({
        schemeName: scheme,
        uniqId,
        skipForbiddenToast: true,
      })
        .then((response) => {
          const data = response.data as unknown
          const object = (Array.isArray(data) ? data[0] : data) as
            | { primary?: string | null }
            | undefined

          return object?.primary || null
        })
        .catch(() => null)
    )
  }

  return titleCache.get(key) as Promise<string | null>
}

export interface ILocationLabel {
  /** page or scheme name, e.g. "Contacts" */
  page: string
  /** name of the record, when the viewer may see it */
  object?: string
  /** in-app link, always a plain pathname */
  path?: string
}

export const useLocationLabel = (
  location?: IUserLocation | null
): ILocationLabel | null => {
  const { t } = useTranslation('presence')
  const schemas = useAppSelect.schemas()
  const [objectTitle, setObjectTitle] = React.useState<string | null>(null)

  const scheme = location?.object?.scheme
  const uniqId = location?.object?.uniqId

  React.useEffect(() => {
    let cancelled = false
    setObjectTitle(null)

    if (scheme && uniqId) {
      resolveObjectTitle(scheme, uniqId).then((title) => {
        if (!cancelled) setObjectTitle(title)
      })
    }

    return () => {
      cancelled = true
    }
  }, [scheme, uniqId])

  if (!location?.path) return null

  const schemeFromPath = /^\/storage\/([^/]+)/.exec(location.path)?.[1]
  const schemeTitle = schemas?.find(
    (item) => item.identifier === (scheme || schemeFromPath)
  )?.title

  const pageKey =
    PAGE_KEYS.find(([pattern]) => pattern.test(location.path as string))?.[1] ||
    'other'

  return {
    page: schemeTitle || t(`pages.${pageKey}`),
    object: scheme && uniqId ? objectTitle || t('team.object') : undefined,
    // presence data is validated by the backend, but a link is a link: pathnames only
    path:
      location.path.startsWith('/') && !location.path.startsWith('//')
        ? location.path
        : undefined,
  }
}
